import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { api, call } from '../../../lib/api'
import { customerQuery } from '../../../lib/queries'
import { ago, fileUrl, kg, nairaFull, nairaShort } from '../../../lib/format'
import { CustomerForm } from '../../../components/customer-form'
import { SupplyPanel } from '../../../components/supply'
import { FlowDemo } from '../../../components/flow-demo'
import { kgStat, Avatar, Button, Card, Modal, PageHeader, StatCard, cx, levelWord, toast } from '../../../components/ui'

export const Route = createFileRoute('/_app/customers/$customerId')({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(customerQuery(params.customerId)),
  component: Profile,
})

function Detail({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="space-y-1.5 text-[15px]">
      <p className="font-semibold tracking-[0.15px] opacity-50">{label}</p>
      <p className="font-medium">{value || '—'}</p>
    </div>
  )
}

// Vertical gauge from the design: fill + marker at the current level, dashed line at the reorder trigger.
function Gauge({ level, capacity, trigger, liveFlow }: { level: number; capacity: number; trigger: number; liveFlow?: number }) {
  const f = capacity > 0 ? Math.min(1, level / capacity) : 0
  const t = capacity > 0 ? Math.min(1, trigger / capacity) : 0
  const below = level <= trigger
  // Keep the level badge clear of the trigger label when the two are close together.
  const badgeTop = Math.abs(f - t) < 0.14 ? `calc(${(1 - f) * 100}% ${f >= t ? '- 18px' : '+ 22px'})` : `${(1 - f) * 100}%`
  return (
    <div className="relative h-[208px] w-[140px]">
      <div className="absolute top-0 bottom-0 left-0 w-[41px] bg-[linear-gradient(to_bottom,rgb(44_199_108/0.15),rgb(248_104_34/0.25)_70%,rgb(240_120_120/0.6))]">
        <div className="absolute inset-x-[16px] bottom-0 bg-[linear-gradient(to_bottom,#ffffff00,#ffffffcc)] transition-[top] duration-700" style={{ top: `${(1 - f) * 100}%` }} />
      </div>
      <span className="absolute left-[10px] grid size-[22px] -translate-y-1/2 place-items-center rounded-full bg-brand ring-4 ring-white transition-[top] duration-700" style={{ top: `${(1 - f) * 100}%` }}>
        <span className="size-2 rounded-full bg-white" />
      </span>
      <span className={cx('absolute left-[76px] -translate-y-1/2 rounded-full px-2 py-1 text-xs font-bold tracking-[0.4px] text-white transition-[top] duration-700', below ? 'bg-danger' : 'bg-sage')} style={{ top: badgeTop }}>
        {level.toFixed(3)}kg
      </span>
      <span className="absolute left-[22px] w-[48px] border-t border-dashed border-ink" style={{ top: `${(1 - t) * 100}%` }} />
      <span className="absolute left-[76px] -translate-y-1/2 leading-4" style={{ top: `${(1 - t) * 100}%` }}>
        <span className="text-[13px] text-[#130a2b]/70">{trigger}</span> <span className="text-[10px] font-bold text-[#130a2b]/30">kg</span>
        <span className="block text-[8px] text-[#999]">trigger volume level</span>
      </span>
      {liveFlow != null && liveFlow > 0 && <span className="absolute -bottom-6 left-0 text-[10px] whitespace-nowrap text-good">● using {liveFlow.toFixed(2)} L/min</span>}
    </div>
  )
}

function Profile() {
  const { customerId } = Route.useParams()
  const navigate = Route.useNavigate()
  const qc = useQueryClient()
  const { data } = useSuspenseQuery(customerQuery(customerId))
  const [editing, setEditing] = useState(false)
  const [flow, setFlow] = useState(false)
  const c = data.customer
  const live = data as typeof data & { liveFlow?: number }
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['customer', customerId] })
    qc.invalidateQueries({ queryKey: ['customers'] })
  }

  const supply = useMutation({
    mutationFn: (kgs: number) => call(api.customers[':id'].supply.$post({ param: { id: customerId }, json: { kg: kgs } })),
    onSuccess: (r) => (toast(`Supplied ${r.kg}kg · ${nairaFull(r.amountKobo)}`), refresh()),
    onError: (e) => toast(e.message, 'error'),
  })
  const invite = useMutation({
    mutationFn: () => call(api.customers[':id'].invite.$post({ param: { id: customerId } })),
    onSuccess: (r) => (toast(r.existingAccount ? `Added to ${c.firstName}'s GasRail account` : `Invite sent to ${c.email}`), refresh()),
    onError: (e) => toast(e.message, 'error'),
  })
  const remove = useMutation({
    mutationFn: () => call(api.customers[':id'].$delete({ param: { id: customerId } })),
    onSuccess: () => (toast('Customer deleted'), qc.invalidateQueries({ queryKey: ['customers'] }), navigate({ to: '/customers' })),
    onError: (e) => toast(e.message, 'error'),
  })
  const approve = useMutation({
    mutationFn: () => call(api.customers[':id'].approve.$post({ param: { id: customerId } })),
    onSuccess: () => (toast(`${c.firstName} is now an active customer`), refresh()),
    onError: (e) => toast(e.message, 'error'),
  })
  const photo = fileUrl(c.photoKey)

  return (
    <div>
    <PageHeader
      title={c.name}
      subtitle={<><Link to="/customers" className="hover:text-brand">Customers</Link> · {kg(c.cylinderKg)} cylinder{data.outlet ? ` · ${data.outlet.name}` : ''}</>}
      actions={<>
        <Button variant="dark" onClick={() => setFlow(true)}>Live gas flow</Button>
        <Button variant="light" onClick={() => setEditing(true)}>Edit customer</Button>
        <Button variant="light" disabled={invite.isPending} onClick={() => invite.mutate()} title={c.email ? `Send ${c.email} a sign-in link` : 'Add an email first'}>
          {data.appAccess === 'active' ? 'On the customer app ✓' : data.appAccess === 'invited' ? 'Resend app invite' : 'Invite to customer app'}
        </Button>
        <Button variant="ghost" className="text-danger" onClick={() => confirm(`Delete ${c.name} and their order history?`) && remove.mutate()}>Delete</Button>
      </>}
    />
    <FlowDemo open={flow} onClose={() => setFlow(false)} devEui={data.device?.devEui} levelKg={c.levelKg} cylinderKg={c.cylinderKg}
      lastSeenAt={data.device?.lastSeenAt} lastFlowSlpm={data.device?.lastFlowSlpm} />
    <div className="grid gap-8 xl:grid-cols-[1fr_430px]">
      <div className="min-w-0">
        <div className="flex flex-col gap-8 sm:flex-row">
          {photo ? (
            <img src={photo} alt={c.name} className="aspect-[258/230] w-full max-w-[258px] rounded-[20px] object-cover shadow-[0_25px_20px_-15px_rgb(0_0_0/0.25)]" />
          ) : (
            <Avatar name={c.name} className="aspect-[258/230] h-auto w-full max-w-[160px] rounded-[20px] text-4xl sm:max-w-[258px] sm:text-5xl" />
          )}
          <div className="grid content-start gap-3.5 sm:pt-4">
            <Detail label="Name" value={c.name} />
            <Detail label="Gender" value={c.gender ? c.gender[0].toUpperCase() + c.gender.slice(1) : null} />
            <Detail label="Address" value={c.address} />
            <Detail label="Phone Number" value={c.phone} />
          </div>
        </div>

        {c.status === 'pending' && (
          <div className="mt-6 flex flex-wrap items-center gap-3 rounded-card bg-peach p-4 text-sm">
            <p className="min-w-0 flex-1"><b>{c.firstName} signed up from the GasRail app</b> and added this {kg(c.cylinderKg)} cylinder. Check it at the outlet, then approve. Edit the customer to link their meter.</p>
            <Button className="h-9" disabled={approve.isPending} onClick={() => approve.mutate()}>Approve customer</Button>
          </div>
        )}

        <div className="mt-8 grid grid-cols-2 gap-4 sm:max-w-[432px] sm:gap-7">
          <StatCard tone="peach" label="All-time Volume" value={kgStat(data.stats.volumeKg)} change={data.stats.volumeChange} />
          <StatCard label="All-time Orders" value={data.stats.orders} change={data.stats.ordersChange} />
          <StatCard label="All-time Revenue" value={nairaShort(data.stats.revenueKobo)} sub={nairaFull(data.stats.revenueKobo)} />
        </div>

        <section className="mt-8 max-w-[460px]">
          <h2 className="mb-3 text-sm font-semibold">Activities</h2>
          <ol className="relative space-y-2 before:absolute before:top-4 before:bottom-4 before:left-4 before:w-px before:bg-black/10">
            {data.activities.map((a) => (
              <li key={a.id} className="relative flex gap-2 p-1">
                <Avatar name={c.name} photoKey={c.photoKey} className="relative size-6 text-[9px]" />
                <div className="min-w-0">
                  <p className="truncate text-sm">{a.text}</p>
                  <p className="text-xs text-muted">{ago(a.at)}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      </div>

      <div className="space-y-8">
        <div className="text-right text-sm font-semibold">
          <p className="text-brand">Outlet Connected with Customer:</p>
          {data.outlet && <Link to="/outlets/$outletId" params={{ outletId: data.outlet.id }} className="mt-2 inline-block text-ink-3 hover:text-brand">{data.outlet.name}</Link>}
        </div>
        <Card className="p-7">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Gas Consumption Levels</h2>
            <span className="text-xs text-muted">{levelWord(c.level)}</span>
          </div>
          <div className="mt-10 flex items-center justify-around gap-4 pb-10">
            <div className="relative size-[150px] flex-none sm:size-[177px]">
              <img src="/img/cylinder-lg.webp" alt="" className="size-full" />
              <p className="absolute inset-x-0 top-[58%] text-center text-sm leading-5 font-semibold"><span className="text-[11px]">Capacity:</span><br />{kg(c.cylinderKg)}</p>
            </div>
            <Gauge level={c.levelKg} capacity={c.cylinderKg} trigger={c.triggerKg} liveFlow={live.liveFlow} />
          </div>
          <p className="text-xs text-muted">
            {data.device ? <>Meter <span className="font-mono">{data.device.devEui}</span> · {data.device.lastSeenAt ? `last reading ${ago(data.device.lastSeenAt)}` : 'waiting for first reading'}</> : 'No meter linked — edit the customer to add its DevEUI.'}
          </p>
        </Card>
        <SupplyPanel
          title="Supply this customer"
          balanceKg={data.outlet?.balanceKg ?? 0}
          pricePerKg={data.pricePerKg}
          maxKg={Math.max(0, Math.round((c.cylinderKg - c.levelKg) * 100) / 100)}
          busy={supply.isPending}
          onSupply={(n) => supply.mutateAsync(n)}
        />
      </div>

      <Modal open={editing} onClose={() => setEditing(false)} title={`Edit ${c.firstName}`} wide>
        <CustomerForm initial={{ ...c, devEui: data.device?.devEui }} onDone={() => setEditing(false)} />
      </Modal>
    </div>
    </div>
  )
}
